import React, { useState, useEffect, useContext } from 'react';
import TronContext from './TronContext';
import { cleanupTronStorage } from './utils/walletCleanup';
import { TronWeb } from 'tronweb';
import { WalletConnectWallet, WalletConnectChainID } from '@tronweb3/walletconnect-tron'
import ApiService from './Services/ApiService';
import PaymentDataContext from './PaymentDataContext.jsx';
import { useDispatch } from 'react-redux';
import { setErrorModal } from './Store/Reducers/errorReducer.js';

// Lazy initialization
let walletConnectWallet = null;
let tronWebInstance = null;
const projectId = import.meta.env.VITE_PROJECT_ID;

const TronComponent = ({ children }) => {
  const dispatch = useDispatch();
  const { paymentData } = useContext(PaymentDataContext);
  const [tronWeb, setTronWeb] = useState(null);
  const [account, setAccount] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    const tw = new TronWeb({ fullHost: 'https://api.trongrid.io' })
    setTronWeb(tw)
  }, [])

  const payError = (err_code, err_message) => {
    ApiService.payError({ Code: `${err_code}`, Message: err_message }).then(() => {
      dispatch(setErrorModal({ show: true, message: err_message, errorType: "" }))
    }).catch((err) => console.log(err))
  }

  const connectWallet = async () => {
    try {
      if (wallet) {
        try { await wallet.disconnect() } catch { /* ignore */ }
      }
      setWallet(null);
      setAccount(null);

      const wc = new WalletConnectWallet({
        network: WalletConnectChainID.Mainnet,
        options: {
          projectId: projectId,
          metadata: {
            name: 'Cryllex',
            description: ' ',
            url: 'https://cryllex.com',
            icons: ['https://cryllex.com/favicon.ico']
          },
          disableProviderPing: true,
        },
        themeMode: 'dark',
      })
      const { address: addr } = await wc?.connect();

      if (!addr || !addr.startsWith('T')) {
        dispatch(setErrorModal({
          show: true,
          message: "Incorrect wallet connected. Please connect a TRON wallet (address starting with 'T') and try again.",
          errorType: ""
        }));
        try {
          await wc.disconnect();
        } catch { /* ignore */ }
        try {
          if (typeof wc.closeModal === 'function') {
            await wc.closeModal();
          }
        } catch { /* ignore */ }
        return false;
      }

      setWallet(wc);
      setAccount(addr);
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  const toUsdtAmount = (value) => {
    return BigInt(Math.round(Number(value) * 1_000_000));
  }

  const sendTransaction = async (to, amount, token) => {
    if (!to || !amount || !token) {
      throw new Error("Missing required parameters");
    }
    if (!wallet || !account || !tronWeb) {
      dispatch(setErrorModal({ show: true, message: "Wallet not connected", errorType: "" }));
      return null;
    }

    if (!account.startsWith('T')) {
      dispatch(setErrorModal({
        show: true,
        message: "Error: Incorrect wallet connected. Please connect a TRON wallet whose address starts with 'T' and try again.",
        errorType: ""
      }));
      return null;
    }

    try {
      if (!tronWeb.isAddress(token.contractAddress)) {
        throw new Error(`Invalid USDT contract address: ${token.contractAddress}`)
      }
      if (!tronWeb.isAddress(to)) {
        throw new Error('Invalid recipient address. Please enter a valid TRON address.')
      }

      // ВАЖНО: address параметр передаём BASE58 (T...), НЕ hex
      const params = [
        { type: 'address', value: to },
        { type: 'uint256', value: toUsdtAmount(+amount).toString() },
      ]

      const { result, transaction } = await tronWeb.transactionBuilder.triggerSmartContract(
        token.contractAddress,
        'transfer(address,uint256)',
        { feeLimit: 200000000 },
        params,
        account
      )

      if (!result?.result || !transaction) {
        const msg = result?.message || 'Transaction trigger failed'
        throw new Error(msg)
      }

      let signed;
      try {
        signed = await wallet.signTransaction(transaction)
        if (!signed) {
          throw new Error('Transaction signing failed - no signed transaction returned')
        }
      } catch (signError) {
        console.log(signError);
        throw new Error(`Transaction signing failed: ${signError?.message || 'Unknown error'}.`)
      }

      // Send the signed transaction
      const sendResult = await tronWeb.trx.sendRawTransaction(signed);
      if (sendResult && sendResult.txid) {
        return sendResult.txid;
      } else {
        const errorMsg = sendResult?.message || 'Transaction send failed'
        throw new Error(errorMsg)
      }
    } catch (err) {
      console.log(err);
      if (paymentData?.status === 0) {
        payError(err.name, err.message);
      }
      return null;
    }
  };

  const disconnectWallet = async () => {
    try {
      if (wallet) {
        await wallet.disconnect();
      }
      setWallet(null);
      setAccount(null);
      cleanupTronStorage();
      console.log('✅ Wallet disconnected');
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      // Still reset state even if disconnect fails
      setTronWeb(null);
      setAccount(null);
    }
  };

  const contextValue = {
    tronWeb: tronWeb || tronWebInstance,
    account,
    connectWallet,
    disconnectWallet,
    sendTransaction,
  };

  return (
    <TronContext.Provider value={contextValue}>
      {children}
    </TronContext.Provider>
  );
};

export default TronComponent;
